import React, { useState, useEffect, useRef } from 'react';
/* 使用 sessionStorage 缓存共享数据，缓存由 shared/cache.ts 管理 */
import SharingIncomeItem from '../../components/sharing-income-item';
import Empty from '../../components/empty';
import { CHARGE_CDN } from '../../../../utils/constants';
import { getIncomeList, getIncomeTotal, querySharePileInfo, queryStationDtl, getUserInfo, getWaitWithdrawAmount } from '../../api';
import { trackGdp } from '../../../../utils/gio-report';
import SharePanel from '../../../../components/share-panel';
import { generatePoster, getRandomItem, getTitleObj } from '../../utils';
import { goToPage } from '../../../../utils/common';
import { queryCurrentUserBasicInfo } from '../../../../api/pile-sharing';
import { shareGuideTextList, titleEnums } from '../../types';
import './index.less'
import { getOrFetchSharePileInfo, getOrFetchStationDtl, getOrFetchCurrentUser } from '../../shared/cache';
import LoadingPage from '../../../../components/loading-page';

const pageSize = 10;

const IncomeRecordPage: React.FC = () => {
  const [userInfo, setUserInfo] = useState<any>({});
  const [incomeList, setIncomeList] = useState<any[]>([]);
  const [incomePageNo, setIncomePageNo] = useState<number>(1);
  const [incomeListTotal, setIncomeListTotal] = useState<number>(0);
  const [incomeTotal, setIncomeTotal] = useState<any>({});
  const [shareGuideText, setShareGuideText] = useState<string>('');
  const [cardBindStatus, setCardBindStatus] = useState<boolean>(false);
  const [stationData, setStationData] = useState<any>({});
  const [sharePanelVisible, setSharePanelVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  // const [stationId, setStationId] = useState<string>('');  // 改为 ref，避免不必要重渲染
  const stationIdRef = useRef<string>('');

  const incomeListRef = useRef(null);
  const incomeListLoading = useRef(false);

  const miniShareTitle = stationData?.baseDto?.stationName || '';

  const isCloseShare = stationData?.operationDto?.stationStatus === '03';

  const getIncomeData = (pageNo = 1) => {
    return new Promise<any>(async (resolve) => {
      if (pageNo === 1) setLoading(true);
      incomeListLoading.current = true;
      try {
        const incomeListRes = await getIncomeList({
          stationId: stationIdRef.current,
          pageNo,
          pageSize
        });
        const incomeTotalRes = await getIncomeTotal({
          stationId: stationIdRef.current,
        });
        if (incomeListRes.code === 200 && incomeListRes?.data) {
          const { unfinishedList = [], finishedList = [] } = incomeListRes.data;
          let toSetList: any[] = [];
          if (pageNo === 1) {
            toSetList = [...unfinishedList, ...finishedList];
            setIncomeList(toSetList);
            setIncomeListTotal(toSetList.length + 1);
          } else {
            toSetList = [...incomeList, ...finishedList];
            setIncomeList(toSetList);
            setIncomeListTotal(finishedList.length >= pageSize ? toSetList.length + 1 : toSetList.length);
          }
        }
        if (incomeTotalRes.code === 200 && incomeTotalRes?.data) {
          setIncomeTotal(incomeTotalRes.data);
        }
        resolve({ incomeTotalData: incomeTotalRes?.data });
      } catch (err) {
        // ignore
      } finally {
        incomeListLoading.current = false;
        if (pageNo === 1) setLoading(false);
      }
    });
  };

  const onIncomeScroll = () => {
    const listDom: any = incomeListRef.current;
    if (listDom) {
      const isEnd = listDom.scrollTop + 50 >= listDom.scrollHeight - listDom.clientHeight;
      if (isEnd) {
        if (incomeList.length >= incomeListTotal) {
          return;
        }
        let nextPage = incomePageNo + 1;
        if (incomePageNo > 1 && (incomePageNo * pageSize > incomeList.length)) {
          nextPage = incomePageNo;
        }
        setIncomePageNo(nextPage);
        if (stationIdRef.current && !incomeListLoading.current) {
          getIncomeData(nextPage);
        }
      }
    }
  };

  const goToBindCard = () => {
    // 跳转已移除：原先跳转逻辑被删除
    trackGdp && trackGdp('P20075', 'B042');
    const { origin, pathname } = window.location;
    goToPage(`${origin}${pathname}#/bind-card`);
  };

  const popSharePanel = () => {
    setSharePanelVisible(true);
  };

  const getUserCardAndWithdrawnAmt = async () => {
    try {
      const userCardRes = await getUserInfo({});
      const withDrawnRes = await getWaitWithdrawAmount({});
      if (userCardRes?.code === 200) {
        setCardBindStatus(!!userCardRes?.data?.userCardRelaDto?.bankAccount);
      }
      // withdrawn amount not displayed on this page; keep call for parity
      // if (withDrawnRes?.code === 200) { /* noop */ }
    } catch (err) {
      console.log('getUserCardAndWithdrawnAmt error', err);
    }
  };

  const getUser = async () => {
    let newUserInfo = {};
    try {
      const data = await getOrFetchCurrentUser(() => queryCurrentUserBasicInfo());
      newUserInfo = data || {};
      // 保持当前页面不清理 localStorage 的原有行为（home 页面负责 uid 变更清理）
    } catch (e) {
      console.log('network error');
    } finally {
      setUserInfo(newUserInfo);
    }
  };

  const initRandomText = () => {
    setShareGuideText(getRandomItem(shareGuideTextList));
  };

  const initPage = async () => {
    try {
      const data = await getOrFetchSharePileInfo(() => querySharePileInfo());
      if (data) {
        const stationRes = await getOrFetchStationDtl(`${data?.stationId}`, (id) => queryStationDtl({ stationId: id, latitude: 0, longitude: 0 }));
        if (stationRes) {
          // 先赋值 ref，再触发渲染（setStationData）即可
          stationIdRef.current = data?.stationId || '';
          setStationData(pre => stationRes);
          // 初始化收益列表与汇总（使用 stationId 发起收益请求）
          getIncomeData();
          getUserCardAndWithdrawnAmt();
        }
      }
    } catch (err) {
      console.log('initPage error', err);
    }
  };

  useEffect(() => {
    document.title = '共享收益';
    getUser();
    initPage();
    initRandomText();
  }, []);

  // 仅当存在已到账记录且未绑定银行卡时，展示绑卡提示
  const idx = incomeList?.findIndex(item => {
    let a = getTitleObj(item).title;
    console.log(titleEnums.TRADE_SUCCESS.title, '---', a);
    return a === titleEnums.TRADE_SUCCESS.title;
  });

  return (
    <div className='income_record-page'>
      <div className='income_record-list' onScroll={onIncomeScroll} ref={incomeListRef}>
        {
          idx !== -1 && !cardBindStatus && (
            <div className='bind-card-tips-wrap' onClick={goToBindCard}>
              <img alt='' src={`${CHARGE_CDN}/sharing/icon_rmb.png`} className='icon-rmb' />
              <div className='text'>录入银行卡信息，确保收益顺利到账</div>
              <img alt='' className='right-icon' src={`${CHARGE_CDN}/sharing/icon_arrow_right_green.png`} />
            </div>
          )
        }
        {
          loading
            ?
            <LoadingPage />
            :
            (
              incomeList.length
                ?
                incomeList.map((item, index) => {
                  return <SharingIncomeItem data={item} key={`${index}_item`} userInfo={userInfo} />
                })
                :
                <div className='income_record-list-empty'>
                  <Empty
                    text={shareGuideText}
                    showShareBtn={!isCloseShare}
                    onShare={() => {
                      popSharePanel()
                      trackGdp('P20075', 'B030')
                    }}
                  />
                </div>
            )
        }
      </div>

      <SharePanel
        visible={sharePanelVisible}
        onClose={() => setSharePanelVisible(false)}
        contentClassName='pile-share-panel'
        shareInfo={{
          shareMainTitle: miniShareTitle,
          shareSubTitle: '',
          shareImgUrl: `${CHARGE_CDN}/sharing/mini_share_v2.jpg`,
          sharePageUrl: `pages/home-pile-station-detail/index?stationId=${stationIdRef.current}`
        }}
        getPosterBase64Data={() => generatePoster(stationData)}
        btns={['wx', 'poster']}
        onClickWx={() => { trackGdp('P20075', 'B023') }}
        onClickPoster={() => { trackGdp('P20075', 'B024') }}
      />
    </div>
  );
};

export default IncomeRecordPage;